const isEmpty = require("lodash/isEmpty");
const get = require("lodash/get");
const omit = require("lodash/omit");
const omitBy = require("lodash/omitBy");
const isNil = require("lodash/isNil");

const eagleCache = require("../../server/facades/eagle/cache");
const logger = require("../../logger");

const GIFTWRAP_ADDON_TYPES = ["giftwrap", "postcard"];

const commaSeperatedRegex = /,\W?/g;

const omitNilAddOnValues = (addOn) =>
  omitBy(addOn, (value) => isNil(value) || isEmpty(value));

const inValidForCountry = (addOn, countryModel) => {
  return (
    addOn.hideOnIsos &&
    addOn.hideOnIsos.includes(countryModel.iso.toLowerCase())
  );
};

const hasAddons = (pages) => {
  return (
    pages.length > 0 &&
    pages[0].items.length > 0 &&
    !isEmpty(omitNilAddOnValues(pages[0].items[0]))
  );
};

const addonMatches = (addOn, addOnToMatch) => {
  if (addOn.ids && addOnToMatch.ids) {
    return addOn.ids.split(commaSeperatedRegex).some((id) => {
      return addOnToMatch.ids.split(commaSeperatedRegex).includes(id);
    });
  }
  return (
    addOn.type === addOnToMatch.type && addOn.design === addOnToMatch.design
  );
};

const findAddOnContent = (addOn, addOns) =>
  addOns.reduce((result, item) => {
    if (!isEmpty(result)) {
      return result;
    }
    if (addonMatches(addOn, item)) {
      let description = isEmpty(get(addOn, "description[0].text", ""))
        ? item.description
        : addOn.description;

      if (typeof description === "string") {
        description = [
          {
            type: "paragraph",
            text: description,
            spans: [],
          },
        ];
      }

      const hideOnIsos = addOn.hideOnIsos ? addOn.hideOnIsos : item.hideOnIsos;

      return {
        ...omit(item, ["formatId"]), // TODO: Remove this old key that is no longer used in prismic
        ...addOn,
        hideOnIsos,
        description,
      };
    }
  }, {});

const omitEmptyContent = (addOn) =>
  omitBy(
    addOn,
    (value, key) =>
      isNil(value) ||
      isEmpty(value) ||
      (key === "description" && !value[0].text)
  );

const buildPages = (product) => {
  const pages = get(product, "addOns.body", []);

  if (!hasAddons(pages)) {
    return [];
  }

  const { addOns } = product;
  const defaultEditingBanner = get(
    addOns,
    "options.defaultAddOns.editingBanner",
    ""
  );
  const defaultContent = get(addOns, "options.defaultAddOns.content", []);
  const defaultButton = get(addOns, "options.defaultAddOns.buttonCta", "");
  const defaultAddOns = get(addOns, "options.defaultAddOns.body[0].items", []);

  return pages
    .filter((page) => page.sliceType === "page")
    .reduce((result, page, idx) => {
      // If the first page is empty/broken then we dont want to build other pages
      if (idx > 0 && isEmpty(result)) {
        return [];
      }

      const isPageContentEmpty =
        isEmpty(page.primary.content) || !page.primary.content[0].text;

      const editingBanner = !product.addOns.editBanner
        ? defaultEditingBanner
        : product.addOns.editBanner;
      const content = isPageContentEmpty
        ? defaultContent
        : page.primary.content;
      const buttonCta = !page.primary.buttonCta
        ? defaultButton
        : page.primary.buttonCta;

      const items = page.items
        .map((addOn) =>
          omitBy(addOn, (value) => isNil(value) || isEmpty(value))
        )
        .reduce((result, addOn) => {
          // Make sure we remove any keys that dont have any value or if there is a description with no value
          addOn = omitEmptyContent(addOn);

          const defaultAddOn = findAddOnContent(addOn, defaultAddOns);

          if (
            isEmpty(defaultAddOn) &&
            !addOn.title &&
            !addOn.description &&
            !addOn.image
          ) {
            return result;
          }

          let content = {
            ...defaultAddOn,
            ...addOn,
            lookInside: get(addOn, "lookInside.content", {}),
          };

          if (!isEmpty(content.lookInside)) {
            // Sometimes it seems Prismic returns a null slide value, here we are making sure that if it does do this
            // or if a content manager adds an empty slide then we remove it just in case
            content.lookInside.slider = content.lookInside.slider
              .filter((slide) => !isNil(slide.image) && !!slide.image.url)
              .map((slide) => slide.image);
          }

          content = omitEmptyContent(content);

          if (!isEmpty(content)) {
            result.push(content);
          }

          return result;
        }, []);

      if (!isEmpty(items)) {
        result.push({
          ...page,
          primary: {
            ...page.primary,
            editingBanner,
            content,
            buttonCta,
          },
          items,
        });
      }

      return result;
    }, []);
};

// We check to see if the addon if valid in eagle.
// This prevents typos and prevents us showing addons that have become unavailable
const getAddonVariantFromEagle = async (countryModel, addOn) => {
  // The design is really the variant sku
  if (addOn.design) {
    try {
      return await eagleCache.variant(countryModel, addOn.design);
    } catch (e) {
      return null;
    }
  }
  return null;
};

const getComponentAddOn = (addOn, variant) => {
  const component = variant.components.find((component) => {
    if (addOn.ids) {
      const addOnIds = addOn.ids.split(commaSeperatedRegex);
      return addOnIds.includes(component.id);
    }

    // The monkey giftwrap id is the only id that has a different structure to other addons
    // where the structure for all other addons are structured as type:size:design, LMN's monkey addon
    // is structured book:type
    if (component.id === "lmn:giftwrap") {
      return addOn.design === "monkey" && addOn.type === "giftwrap";
    }

    return (
      component.id.includes(addOn.design) && component.id.includes(addOn.type)
    );
  });

  if (component) {
    return {
      ...component,
      ...addOn,
    };
  }
};

const buildAddons = async (variant, content, countryModel) => {
  const pages = buildPages(content);

  if (isEmpty(pages)) {
    return [];
  }

  const pageResult = [];

  for (const page of pages) {
    const addOnResult = [];
    for (const addOn of page.items) {
      if (
        addOn.visibleOn &&
        // TODO: Once the eagle variant.formatId is deprecated
        // we will need to remove it from here and refactor this.
        !addOn.visibleOn.includes(variant.formatId) &&
        !addOn.visibleOn.includes(variant.formatIdentifier)
      ) {
        continue;
      }

      // Some addons (crayons, lala lamb) aren't allowed in some countries that inherit
      // from the base UK locale.  This gives us a the opportunity to hide an addon based on
      // the locale
      if (inValidForCountry(addOn, countryModel)) {
        continue;
      }

      // Check if addon is part of the components section of the variant
      const componentAddOn = getComponentAddOn(addOn, variant);
      if (componentAddOn) {
        addOnResult.push(componentAddOn);
        continue;
      }

      // Check if add on is valid via eagle.
      const variantFromEagle = await getAddonVariantFromEagle(
        countryModel,
        addOn
      );
      if (variantFromEagle) {
        addOnResult.push({
          ...variantFromEagle,
          ...addOn,
        });
        continue;
      }

      // if we have got to this stage the addon in prismic is incorrect.  We want to flag any errors so we
      // can correct prismic. This will also error if a product is removed from eagle so might be chatty but
      // let's see how many errors we get and adjust as we go.
      logger.warn(
        `Addon incorrect for variant ${variant.id} and addon ${addOn.design}`
      );
    }

    if (addOnResult.length > 0) {
      pageResult.push({
        ...page,
        items: addOnResult,
      });
    }
  }

  return pageResult;
};

const getPageIndexOfType = (pages = [], addonTypes = []) => {
  let pageIdx = -1;
  pages.some((page, idx) => {
    const hasGiftwrap = page.items.some((item) =>
      addonTypes.includes(item.type)
    );
    if (hasGiftwrap) {
      pageIdx = idx;
      return true;
    }
  });
  return pageIdx;
};

const getPageIndexNotOfType = (pages = [], addonTypes = []) => {
  let pageIdx = -1;
  pages.some((page, idx) => {
    const hasGiftwrap = page.items.some(
      (item) => !addonTypes.includes(item.type)
    );
    if (hasGiftwrap) {
      pageIdx = idx;
      return true;
    }
  });
  return pageIdx;
};

const getAddonsOfType = (addons, types) => {
  if ((!addons || isEmpty(addons)) && (!types || isEmpty(types))) {
    return [];
  }
  return addons.filter((addon) =>
    types.some((type) => addon.id.includes(type))
  );
};

const getAddonsNotOfType = (addons, types) => {
  if ((!addons || isEmpty(addons)) && (!types || isEmpty(types))) {
    return [];
  }

  return addons.filter((addon) =>
    types.every((type) => !addon.id.includes(type))
  );
};

const getGiftwrapIndex = (pages) => {
  let pageIdx = getPageIndexOfType(pages, GIFTWRAP_ADDON_TYPES);
  return pageIdx === -1 ? null : pageIdx;
};

const getExtrasIndex = (pages) => {
  let pageIdx = getPageIndexNotOfType(pages, GIFTWRAP_ADDON_TYPES);
  return pageIdx === -1 ? null : pageIdx;
};

module.exports = {
  GIFTWRAP_ADDON_TYPES,
  getAddonsOfType,
  getAddonsNotOfType,
  omitNilAddOnValues,
  hasAddons,
  addonMatches,
  findAddOnContent,
  omitEmptyContent,
  getGiftwrapIndex,
  getExtrasIndex,
  buildAddons,
  getComponentAddOn,
  buildPages,
};
